import { ChangeDetectionStrategy, Component, signal, TemplateRef } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import type { TooltipPosition } from './tooltip.directive';

/**
 * Internal component rendered inside the CDK overlay.
 * Not meant to be used directly - use the TooltipDirective instead.
 */
@Component({
  selector: 'zag-tooltip',
  imports: [NgTemplateOutlet],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.zag-tooltip]': 'true',
    '[class.zag-tooltip--above]': 'position() === "above"',
    '[class.zag-tooltip--below]': 'position() === "below"',
    '[class.zag-tooltip--left]': 'position() === "left"',
    '[class.zag-tooltip--right]': 'position() === "right"',
    role: 'tooltip',
  },
  styles: `
    :host {
      display: block;
      padding: var(--spacing-xs, 6px) var(--spacing-sm, 10px);
      background-color: var(--surface-color-primary, #fff);
      color: var(--text-color-primary, #223b52);
      font-size: var(--font-size-xs, 12px);
      font-family: var(--font-family-primary, sans-serif);
      line-height: 1.4;
      border: var(--border-width-default, 1px) solid var(--border-color-default, #e2e8f0);
      border-radius: var(--border-radius-md, 6px);
      box-shadow: var(--shadow-md, 0 4px 12px rgba(0, 0, 0, 0.1));
      word-wrap: break-word;
      overflow-wrap: break-word;
      max-width: 320px;
      animation: tooltip-fade-in 0.15s ease-out;
    }

    :host:not(:has(> *)) {
      white-space: pre-line;
    }

    @keyframes tooltip-fade-in {
      from {
        opacity: 0;
        transform: scale(0.95);
      }
      to {
        opacity: 1;
        transform: scale(1);
      }
    }
  `,
  template: `
    @if (templateRef()) {
      <ng-container *ngTemplateOutlet="templateRef()!" />
    } @else {
      {{ text() }}
    }
  `,
})
export class TooltipComponent {
  /**
   * Text content for the tooltip.
   */
  readonly text = signal<string>('');

  /**
   * Template reference for custom tooltip content.
   */
  readonly templateRef = signal<TemplateRef<unknown> | null>(null);

  /**
   * Position of the tooltip (used for styling/arrows if needed).
   */
  readonly position = signal<TooltipPosition>('above');
}
